/**
 * Diagnostics for the chat widget.
 *
 * Deliberately quiet by default in production — an embedded widget must not
 * spam a customer's console — while surfacing anything that indicates a real
 * fault. `onLog` lets a host pipe entries into its own telemetry.
 */
const RANK = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
    silent: 100,
};
export function createLogger(options = {}) {
    const level = options.level ?? defaultLevel();
    const threshold = RANK[level];
    const sink = options.sink ?? consoleSink;
    const prefix = options.prefix ?? '';
    const emit = (severity, message, detail) => {
        if (RANK[severity] < threshold)
            return;
        try {
            sink({
                level: severity,
                message: prefix ? `${prefix} ${message}` : message,
                detail,
                at: Date.now(),
            });
        }
        catch {
            // A broken sink must never take the chat down with it.
        }
    };
    return {
        debug: (m, d) => emit('debug', m, d),
        info: (m, d) => emit('info', m, d),
        warn: (m, d) => emit('warn', m, d),
        error: (m, d) => emit('error', m, d),
        scope: (name) => createLogger({ ...options, level, prefix: prefix ? `${prefix} ${name}` : name }),
    };
}
/** Verbose while developing, anomalies-only once built. */
function defaultLevel() {
    try {
        return import.meta.env?.DEV ? 'debug' : 'warn';
    }
    catch {
        return 'warn';
    }
}
const consoleSink = (entry) => {
    const method = entry.level === 'debug' ? 'log' : entry.level;
    const line = `[agentic-chat] ${entry.message}`;
    if (!entry.detail) {
        console[method](line);
        return;
    }
    // Browsers render a passed object as an expandable tree, which is what we
    // want. Node collapses anything past depth 2 to "[Object]", hiding exactly
    // the content blocks worth inspecting — so print it fully there instead.
    if (typeof window === 'undefined' && typeof console.dir === 'function') {
        console[method](line);
        console.dir(entry.detail, { depth: null });
        return;
    }
    console[method](line, entry.detail);
};
/** Used where a logger is optional, so call sites never need a null check. */
export const noopLogger = {
    debug: () => { },
    info: () => { },
    warn: () => { },
    error: () => { },
    scope: () => noopLogger,
};
